/**
 * COMO Travel API Service
 * Provides functions to fetch data from the Django backend
 */

const API_BASE = '/api';

// Types
export interface Destination {
    id: number;
    name: string;
    slug: string;
    slug_ar?: string;
    image: string;
    description?: string;
    articles?: Article[];
    package_count?: number;
}

export interface Article {
    id: number;
    destination: number;
    title: string;
    content: string;
    author: string;
    date: string;
    quote: string;
    image: string;
}

export interface Package {
    id: number;
    destinations: number[];  // Array of destination IDs (ManyToMany)
    destinations_list: { id: number; name: string; slug: string }[];  // For display
    slug: string;
    slug_ar?: string;
    title: string;
    description: string;
    image: string;
    date: string;
    price: number;
    nights: number;
    tags: string[];
    itinerary: string[];
    includes: string[];
    includes_display: string[];
    curr?: string;
    currency?: string;
}

export interface Activity {
    id: number;
    title: string;
    subtitle: string;
    image: string;
    video?: string;
    logo_title?: string;
    logo_sub?: string;
    promo?: string;
    has_overlay: boolean;
}

// API Functions
export const fetchDestinations = async (lang: string = 'en'): Promise<Destination[]> => {
    const response = await fetch(`${API_BASE}/destinations/?lang=${lang}`);
    if (!response.ok) throw new Error('Failed to fetch destinations');
    return response.json();
};

export const fetchDestination = async (slug: string, lang: string = 'en'): Promise<Destination> => {
    const response = await fetch(`${API_BASE}/destinations/${encodeURIComponent(slug)}/?lang=${lang}`);
    if (!response.ok) throw new Error('Failed to fetch destination');
    return response.json();
};

export const fetchArticles = async (lang: string = 'en', destinationId?: number): Promise<Article[]> => {
    const url = destinationId
        ? `${API_BASE}/articles/?lang=${lang}&destinations=${destinationId}`
        : `${API_BASE}/articles/?lang=${lang}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch articles');
    return response.json();
};

export const fetchPackages = async (lang: string = 'en', destinationId?: number): Promise<Package[]> => {
    const url = destinationId
        ? `${API_BASE}/packages/?lang=${lang}&destinations=${destinationId}`
        : `${API_BASE}/packages/?lang=${lang}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch packages');
    return response.json();
};

export const fetchPackage = async (slug: string, lang: string = 'en'): Promise<Package> => {
    const response = await fetch(`${API_BASE}/packages/${encodeURIComponent(slug)}/?lang=${lang}`);
    if (!response.ok) throw new Error('Failed to fetch package');
    return response.json();
};

export const fetchActivities = async (lang: string = 'en'): Promise<Activity[]> => {
    const response = await fetch(`${API_BASE}/activities/?lang=${lang}`);
    if (!response.ok) throw new Error('Failed to fetch activities');
    return response.json();
};
